import React from 'react'
import {
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  Snackbar,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  MenuItem,
  InputAdornment,
} from '@mui/material'
import { IReduxState } from 'interfaces/IReduxState'
import { useDispatch, useSelector } from 'react-redux'
import MuiAlert from '@mui/material/Alert'
import API from '@aws-amplify/api'
import { VALUE_IN, VALUE_OUT, VALUE_SETENGAH_JADI } from 'enums/stockCard'
import ProductFinder from './productFinder'
import {
  IProductRequest,
  ProductRequest,
  ProductRequestSourceOrDestination,
} from 'interfaces/IProductRequest'
import LoadPremixAutocomplete from 'shared/autocomplete/loadPremixAutocomplete'
import { IPremix } from 'interfaces/IPremix'
import { IProduct } from 'interfaces/IProduct'
import LoadPackagingAutocomplete from 'shared/autocomplete/loadPackagingAutocomplete'
import LoadBatchAutocomplete from 'shared/autocomplete/loadBatchAutocomplete'
import { NumberFormat } from '../numberFormat'

export default function ProductRequestForm() {
  const user = useSelector((state: IReduxState) => state.currentUser || {})
  const [productRequest, setProductRequest] = React.useState<IProductRequest>(
    new ProductRequest({ fillingUnit: 'PCS' })
  )
  const [alertMessage, setAlertMessage] = React.useState('')
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] =
    React.useState(false)
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] =
    React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [hasValidationErrors, setHasValidationErrors] = React.useState(false)

  const extractErrorMessage = (error: any): string => {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.message ||
      error?.message

    if (Array.isArray(apiMessage)) {
      return apiMessage.join(', ')
    }

    return typeof apiMessage === 'string'
      ? apiMessage
      : 'Gagal membuat request produk.'
  }
  const dispatch = useDispatch()
  React.useEffect(() => {
    dispatch({ type: 'SET_PAGE_TITLE', payload: 'Request STJ In/Out' })
  }, [dispatch])

  const handleSnackbarClose = (): void => {
    setIsFailureSnackbarOpen(false)
    setIsSuccessSnackbarOpen(false)
  }
  const getMissingFields = (req: IProductRequest): string[] => {
    const missing: string[] = []
    const inOut = req?.inOutDirection
    if (!inOut) missing.push('In/Out')
    if (!req?.sourceOrDestination) missing.push('Dari/Tujuan')

    // Require notes for options that state "wajib tulis" (RnD and no-cost variants)
    const requiresNotes = [
      ProductRequestSourceOrDestination.rnd,
      ProductRequestSourceOrDestination.noCostIn,
      ProductRequestSourceOrDestination.noCostOut,
    ]
    if (
      req?.sourceOrDestination &&
      requiresNotes.includes(req.sourceOrDestination as any) &&
      !req.notes
    ) {
      missing.push('Notes Peminta')
    }

    const hasManualInput =
      req.premixId &&
      req.packagingId &&
      req.batchNumber &&
      Number(req.quantity || 0) > 0

    const hasProducts = Array.isArray(req.products)
      ? req.products.some((p: any) => Number(p?.qty || 0) > 0)
      : false

    if (!hasManualInput && !hasProducts) {
      if (!req.premixId) missing.push('Ruahan')
      if (!req.packagingId) missing.push('Kemasan')
      if (!req.batchNumber) missing.push('No Bets')
      if (!req.quantity) missing.push('Qty')
    }

    if (hasManualInput && !req.fillingAmount) {
      missing.push('Filling Amount')
    }

    return missing
  }

  const getValidationErrors = () => {
    const errors: string[] = []
    const missing = getMissingFields(productRequest)
    if (missing.length > 0) {
      errors.push(`Lengkapi data berikut: ${missing.join(', ')}`)
    }
    if (hasValidationErrors) {
      errors.push('Ada produk dengan qty yang melebihi stok')
    }
    return errors
  }

  const isAllFieldsComplete = () => {
    return getValidationErrors().length === 0
  }

  const handlePremixChange = async (premix: IPremix) => {
    handleProductRequestFieldChange(premix?.premixId ?? '', 'premixId')
    handleProductRequestFieldChange('', 'batchNumber')
    // Clear any previously selected products so the ProductFinder resets
    handleProductRequestFieldChange([], 'products')
  }

  const handleInOutChange = (value: string) => {
    setProductRequest((prev) => {
      if (value === VALUE_OUT) {
        return {
          ...prev,
          inOutDirection: value,
          sourceOrDestination: '',
          batchNumber: '',
          packagingId: '',
          fillingAmount: null,
          fillingUnit: prev.fillingUnit || 'PCS',
          quantity: null,
        }
      }

      return {
        ...prev,
        inOutDirection: value,
        sourceOrDestination: '',
        products: [],
      }
    })
  }

  const saveProductRequest = () => {
    setIsLoading(true)
    const isOut = productRequest.inOutDirection === VALUE_OUT
    const hasManualInput =
      productRequest.premixId &&
      productRequest.packagingId &&
      productRequest.batchNumber &&
      Number(productRequest.quantity || 0) > 0

    const manualProduct =
      !isOut && hasManualInput
        ? {
          batchNumber: productRequest.batchNumber,
          fillingAmount: Number(productRequest.fillingAmount),
          fillingUnit: productRequest.fillingUnit || 'PCS',
          qty: Math.floor(Number(productRequest.quantity || 0)),
          premixId: productRequest.premixId,
          packagingId: productRequest.packagingId,
        }
        : null

    const finalProducts = isOut
      ? [...(productRequest.products || [])]
      : manualProduct
        ? [manualProduct]
        : []
    const payload: IProductRequest = {
      ...productRequest,
      ...(isOut
        ? {
          batchNumber: '',
          packagingId: '',
          fillingAmount: null,
          quantity: null,
        }
        : { products: [] }),
      products: finalProducts,
    }

    API.post('products', `/products/productRequest`, {
      body: {
        ...payload,
      },
    })
      .then((response) => {
        setAlertMessage('Berhasil membuat request produk.')
        setIsSuccessSnackbarOpen(true)
        setProductRequest(new ProductRequest({ fillingUnit: 'PCS' }))
        // window.location.reload()
      })
      .catch((error) => {
        setAlertMessage(extractErrorMessage(error))
        setIsFailureSnackbarOpen(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const onSelectedProductChange = (products: Partial<IProduct>[]) => {
    // For OUT flow, normalize ProductFinder selection into IProductRequestProduct[] shape
    if (productRequest.inOutDirection === VALUE_OUT) {
      const mapped = (products || [])
        .map((p) => {
          const rawRequested = Number(p.productQty || 0)
          const requestedQty = Number.isFinite(rawRequested)
            ? Math.max(0, Math.floor(rawRequested))
            : 0
          const rawInv: any = p.inventory as any
          let available = 0
          if (typeof rawInv === 'string') {
            const parsed = Number((rawInv as string).replace(/[^0-9.-]/g, ''))
            available = Number.isFinite(parsed) ? parsed : 0
          } else {
            available = Number(rawInv || 0)
          }

          const finalQty = Math.max(
            0,
            Math.min(requestedQty, Math.floor(Number(available || 0)))
          )

          return {
            productId: p.productId,
            productName: String(p.productName || ''),
            qty: Math.floor(Number(finalQty || 0)),
            packagingId: String(
              p.packagingId || productRequest.packagingId || ''
            ),
            premixId: String(p.premixId || productRequest.premixId || ''),
            batchNumber: String((p as any).batchNumber || ''),
            fillingAmount: (p as any).fillingAmount ?? null,
            fillingUnit: String((p as any).fillingUnit || ''),
            unit: String(p.unit || ''),
          }
        })
        .filter((p) => Number(p.qty || 0) > 0)
      handleProductRequestFieldChange(mapped, 'products')
    } else {
      // IN flow or other: pass through
      handleProductRequestFieldChange(products as any, 'products')
    }
  }

  const handleProductRequestFieldChange = (value: any, key: string): void => {
    setProductRequest((data: any) => ({
      ...data,
      [key]: value,
    }))
  }

  return (
    <React.Fragment>
      <Paper>
        <Box p={3}>
          <List>
            <ListItem>
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  <Typography>
                    <Box fontWeight="bold" display="inline">
                      In/Out
                    </Box>
                  </Typography>
                </FormLabel>
                <RadioGroup
                  row
                  aria-label="position"
                  value={productRequest?.inOutDirection}
                  onChange={(e) => handleInOutChange(e.target.value)}
                >
                  <FormControlLabel
                    value={VALUE_IN}
                    control={<Radio color="primary" />}
                    label={'Masuk ke Gudang Setengah Jadi'}
                  />
                  <FormControlLabel
                    value={VALUE_OUT}
                    control={<Radio color="primary" />}
                    label={'Keluar dari Gudang Setengah Jadi'}
                  />
                </RadioGroup>
              </FormControl>
            </ListItem>
            <ListItem>
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  <Typography>
                    <Box fontWeight="bold" display="inline">
                      Dari/Tujuan
                    </Box>
                  </Typography>
                </FormLabel>
                <RadioGroup
                  row
                  aria-label="position"
                  value={productRequest?.sourceOrDestination}
                  onChange={(e) =>
                    handleProductRequestFieldChange(
                      e.target.value,
                      'sourceOrDestination'
                    )
                  }
                >
                  <FormControlLabel
                    value={ProductRequestSourceOrDestination.rnd}
                    control={<Radio color="primary" />}
                    label={'Rnd (wajib tulis nomor permintaan rnd)'}
                  />
                  <FormControlLabel
                    value={ProductRequestSourceOrDestination.qc}
                    control={<Radio color="primary" />}
                    label={'QC'}
                  />
                  <FormControlLabel
                    value={ProductRequestSourceOrDestination.noCostIn}
                    control={<Radio color="primary" />}
                    label={
                      'Barang masuk tanpa biaya (wajib tulis notes dari siapa)'
                    }
                  />
                  <FormControlLabel
                    value={ProductRequestSourceOrDestination.noCostOut}
                    control={<Radio color="primary" />}
                    label={
                      'Barang di keluarkan tanpa biaya (wajib tulis notes untuk siapa)'
                    }
                  />
                  <FormControlLabel
                    value={
                      ProductRequestSourceOrDestination.fillingChangePackaging
                    }
                    control={<Radio color="primary" />}
                    label={'Filling - Ganti Kemasan'}
                  />
                  <FormControlLabel
                    value={ProductRequestSourceOrDestination.fillingRework}
                    control={<Radio color="primary" />}
                    label={'Filling - Rework'}
                  />
                  <FormControlLabel
                    value={
                      ProductRequestSourceOrDestination.productionAdditionalRequest
                    }
                    control={<Radio color="primary" />}
                    label={'Produksi - Additional Request'}
                  />
                  <FormControlLabel
                    value={ProductRequestSourceOrDestination.sampleInhouse}
                    control={<Radio color="primary" />}
                    label={'Sample - In house'}
                  />
                  <FormControlLabel
                    value={ProductRequestSourceOrDestination.adjustment}
                    control={<Radio color="primary" />}
                    label={'Adjustment'}
                  />
                  <FormControlLabel
                    value={ProductRequestSourceOrDestination.labelingAdditional}
                    control={<Radio color="primary" />}
                    label={'Labeling: Minta Tambahan'}
                  />
                </RadioGroup>
              </FormControl>
            </ListItem>
            <Box>
              <ListItem>
                <LoadPremixAutocomplete
                  name={productRequest.premixId}
                  onChange={handlePremixChange}
                />
              </ListItem>
              {productRequest.inOutDirection === VALUE_OUT && (
                <ListItem>
                  <LoadPackagingAutocomplete
                    name={productRequest.packagingId}
                    onChange={(e) =>
                      handleProductRequestFieldChange(
                        e?.packagingId,
                        'packagingId'
                      )
                    }
                  />
                </ListItem>
              )}
              {productRequest.inOutDirection === VALUE_IN && (
                <>
                  <ListItem>
                    <LoadBatchAutocomplete
                      name={productRequest.batchNumber}
                      premixId={productRequest.premixId}
                      loadAll
                      onInputChange={(value, reason) => {
                        if (reason === 'input' || reason === 'clear') {
                          handleProductRequestFieldChange(value, 'batchNumber')
                        }
                      }}
                      onChange={(e) =>
                        handleProductRequestFieldChange(
                          e?.batchNumber,
                          'batchNumber'
                        )
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <LoadPackagingAutocomplete
                      name={productRequest.packagingId}
                      onChange={(e) =>
                        handleProductRequestFieldChange(
                          e?.packagingId,
                          'packagingId'
                        )
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <TextField
                      variant="outlined"
                      label="Filling Amount"
                      fullWidth
                      value={productRequest.fillingAmount || ''}
                      InputProps={{
                        inputComponent: NumberFormat as any,
                        inputProps: { decimalScale: 12 },
                      }}
                      onChange={(e) =>
                        handleProductRequestFieldChange(
                          e.target.value,
                          'fillingAmount'
                        )
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <TextField
                      select
                      variant="outlined"
                      label="Filling Unit"
                      fullWidth
                      value={productRequest.fillingUnit || 'PCS'}
                      onChange={(e) =>
                        handleProductRequestFieldChange(
                          String(e.target.value || 'PCS'),
                          'fillingUnit'
                        )
                      }
                    >
                      <MenuItem value="PCS">PCS</MenuItem>
                      <MenuItem value="KG">KG</MenuItem>
                    </TextField>
                  </ListItem>
                  <ListItem>
                    <TextField
                      variant="outlined"
                      label="Qty"
                      fullWidth
                      type="number"
                      inputProps={{ step: 1, min: 0 }}
                      value={productRequest.quantity || ''}
                      onChange={(e) =>
                        handleProductRequestFieldChange(
                          e.target.value,
                          'quantity'
                        )
                      }
                    />
                  </ListItem>
                </>
              )}
            </Box>

            {productRequest.inOutDirection === VALUE_OUT &&
              productRequest.premixId && (
                <ListItem>
                  <Box width="100%">
                    <Typography variant="h6" gutterBottom>
                      Pilih dari Stok yang Ada
                    </Typography>
                    <ProductFinder
                      brandId=""
                      showQty
                      selectedProducts={productRequest?.products}
                      getSelectedProducts={onSelectedProductChange}
                      editableQty
                      showInventory
                      showSelectedProducts
                      hideSearch
                      onValidationChange={setHasValidationErrors}
                      packagingIdFilter={productRequest.packagingId}
                      premixIdFilter={productRequest.premixId}
                      type={VALUE_SETENGAH_JADI}
                    />
                  </Box>
                </ListItem>
              )}
            <ListItem>
              <TextField
                autoComplete="off"
                variant="outlined"
                value={productRequest?.notes}
                fullWidth
                label={'Notes Peminta'}
                onChange={(e) =>
                  handleProductRequestFieldChange(e.target.value, 'notes')
                }
              />
            </ListItem>
            <ListItem>
              <Typography>Request Dibuat Oleh: {user.username}</Typography>
            </ListItem>
          </List>
          {/* Missing fields helper */}
          {getMissingFields(productRequest).length > 0 && (
            <Box mt={2}>
              <MuiAlert severity="warning" elevation={0} variant="outlined">
                Lengkapi data berikut sebelum menyimpan:{' '}
                {getMissingFields(productRequest).join(', ')}
              </MuiAlert>
            </Box>
          )}

          {/* Validation errors */}
          {hasValidationErrors && (
            <Box mt={2}>
              <MuiAlert severity="error" elevation={0} variant="outlined">
                Ada produk dengan qty yang melebihi stok. Mohon perbaiki sebelum
                menyimpan.
              </MuiAlert>
            </Box>
          )}

          <Box
            p={2}
            mt={2}
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
          >
            <Button
              variant="contained"
              color="primary"
              disabled={!isAllFieldsComplete() || isLoading}
              onClick={saveProductRequest}
            >
              Save
            </Button>
            {!isAllFieldsComplete() && (
              <Box mt={1}>
                {getValidationErrors().map((err) => (
                  <Typography
                    key={err}
                    variant="caption"
                    color="error"
                    display="block"
                    align="right"
                  >
                    • {err}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
      <Snackbar
        open={isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </React.Fragment>
  )
}
